import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Bot, Check, Copy, Loader2, Unplug } from "lucide-react";
import { authHeaders, getBackendUrl } from "../lib/http";

// ChatGPT / Claude baglantisi (MCP): baglanti adresi + kurulum, varsayilanlar,
// arac izinleri, bagli istemciler ve son cagrilar.

interface McpInfo {
  url: string;
  tools: Array<{ name: string; title: string; write: boolean }>;
  settings: { defaultImageModel?: string; shopId?: string; disabledTools?: string[] };
  models: Array<{ id: string; name: string; price?: string }>;
  shops: Array<{ id: string; name: string; platform: string; isDefault: boolean }>;
  connections: Array<{ clientId: string; name: string; connectedAt: string; lastUsedAt: string | null }>;
}
interface CallLog {
  id: string; method: string; tool: string; requestId: string | null; ok: boolean; ms: number;
  errorCode: string | null; error: string | null; serverVersion: string | null; createdAt: string;
}
interface McpStatus {
  serverVersion: string; workflowVersion: string; liveSchemaHash: string | null;
  clients: Array<{ clientId: string; schemaHash: string; fetchedAt: string; fetchedFromVersion: string | null; current: boolean }>;
  lastToolsList: { at: string; serverVersion: string | null } | null;
  rejectedAuth: Array<{ at: string; hadBearer: boolean; userAgent: string; method: string | null }>;
}

async function call<T>(path: string, method = "GET", body?: unknown): Promise<T> {
  const r = await fetch(`${getBackendUrl()}/api/mcp${path}`, { method, headers: authHeaders(), body: body ? JSON.stringify(body) : undefined });
  const j = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(j.error || j.message || `İstek başarısız (${r.status})`);
  return j.data as T;
}
const fmt = (d?: string | null) => (d ? new Date(d).toLocaleString("tr-TR", { dateStyle: "short", timeStyle: "short" }) : "—");

export default function McpConnectorPage() {
  const qc = useQueryClient();
  const { data: info, isLoading } = useQuery({ queryKey: ["mcp-info"], queryFn: () => call<McpInfo>("") });
  const { data: logs = [] } = useQuery({ queryKey: ["mcp-logs"], queryFn: () => call<CallLog[]>("/logs"), refetchInterval: 15000 });
  const { data: status } = useQuery({ queryKey: ["mcp-status"], queryFn: () => call<McpStatus>("/status"), refetchInterval: 30000 });
  const [copied, setCopied] = useState(false);
  const saveM = useMutation({
    mutationFn: (s: McpInfo["settings"]) => call<McpInfo["settings"]>("/settings", "PUT", s),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["mcp-info"] }),
  });
  const revokeM = useMutation({
    mutationFn: (clientId: string) => call(`/connections/${encodeURIComponent(clientId)}`, "DELETE"),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["mcp-info"] }),
  });

  if (isLoading || !info) {
    return <div className="flex items-center gap-2 p-8 text-sm text-gray-500"><Loader2 className="h-4 w-4 animate-spin" /> Yükleniyor…</div>;
  }
  const s = info.settings;
  const disabled = new Set(s.disabledTools ?? []);
  const save = (patch: Partial<McpInfo["settings"]>) => saveM.mutate({ ...s, ...patch });
  const toggleTool = (name: string) => {
    const next = new Set(disabled);
    if (next.has(name)) next.delete(name); else next.add(name);
    save({ disabledTools: [...next] });
  };
  const copy = () => {
    void navigator.clipboard.writeText(info.url).then(() => { setCopied(true); setTimeout(() => setCopied(false), 2000); });
  };

  return (
    <div className="mx-auto max-w-4xl space-y-5 p-6">
      <div>
        <h1 className="flex items-center gap-2 text-xl font-semibold text-gray-900"><Bot className="h-6 w-6 text-indigo-600" /> ChatGPT / MCP Bağlantısı</h1>
        <p className="mt-1 text-sm text-gray-500">
          ChatGPT (ya da Claude) panele bağlanır: analizlerden tasarım üretir, taslak oluşturur ve Etsy SEO uyumlu başlık/etiket/açıklama yazar.
        </p>
      </div>

      {/* Kurulum */}
      <section className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
        <h2 className="mb-3 text-sm font-semibold text-gray-800">Bağlantı adresi</h2>
        <div className="mb-4 flex items-center gap-2">
          <code className="flex-1 truncate rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-800">{info.url}</code>
          <button onClick={copy} className="flex items-center gap-1.5 rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50">
            {copied ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />} {copied ? "Kopyalandı" : "Kopyala"}
          </button>
        </div>
        <div className="grid gap-4 md:grid-cols-2">
          <div>
            <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-gray-500">ChatGPT (Plus / Pro)</p>
            <ol className="list-decimal space-y-1 pl-4 text-sm text-gray-700">
              <li>Tarayıcıda <b>chatgpt.com/plugins</b> sayfasını aç (Geliştirici modu artık gerekmiyor).</li>
              <li><b>Ekle</b> → <b>MCP Uygulaması Oluştur</b>: bir ad ver, yukarıdaki adresi yapıştır, kimlik doğrulama <b>OAuth</b> (istemci kimliği/gizli anahtar boş kalsın).</li>
              <li>Açılan panel ekranında <b>İzin ver</b>'e bas.</li>
              <li>Sohbette araçlardan bu eklentiyi seçip kullan.</li>
            </ol>
          </div>
          <div>
            <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-gray-500">Claude</p>
            <ol className="list-decimal space-y-1 pl-4 text-sm text-gray-700">
              <li>Ayarlar → Bağlayıcılar → Özel bağlayıcı ekle.</li>
              <li>Adresi yapıştır, bağlan ve panel ekranında izin ver.</li>
            </ol>
          </div>
        </div>
      </section>

      {/* Varsayilanlar */}
      <section className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
        <h2 className="mb-3 text-sm font-semibold text-gray-800">Varsayılanlar</h2>
        <div className="grid gap-4 md:grid-cols-2">
          <div className="block">
            <span className="mb-1 block text-xs font-medium text-gray-600">Tasarım üretimi</span>
            <p className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-xs text-gray-600">
              ChatGPT panelde görsel üretemez (OpenRouter kapalı): tasarımı kendisi üretip panele gönderir.
            </p>
          </div>
          <label className="block">
            <span className="mb-1 block text-xs font-medium text-gray-600">Mağaza (taslak ve şablonlar bu mağazanın)</span>
            <select value={s.shopId ?? ""} onChange={(e) => save({ shopId: e.target.value || undefined })}
              className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm">
              <option value="">Varsayılan mağaza</option>
              {info.shops.map((sh) => <option key={sh.id} value={sh.id}>{sh.name}{sh.isDefault ? " (varsayılan)" : ""}</option>)}
            </select>
          </label>
        </div>
      </section>

      {/* Arac izinleri */}
      <section className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
        <div className="mb-3 flex items-center justify-between">
          <h2 className="text-sm font-semibold text-gray-800">Araçlar</h2>
          {saveM.isPending && <Loader2 className="h-4 w-4 animate-spin text-gray-400" />}
        </div>
        <p className="mb-3 text-xs text-gray-500">Kapatılan araç ChatGPT'ye hiç gösterilmez. Değişiklik bir sonraki istekte geçerli olur.</p>
        <div className="grid gap-1.5 md:grid-cols-2">
          {info.tools.map((t) => (
            <label key={t.name} className="flex items-center gap-2 rounded-lg px-2 py-1.5 text-sm hover:bg-gray-50">
              <input type="checkbox" checked={!disabled.has(t.name)} onChange={() => toggleTool(t.name)} className="rounded border-gray-300 text-indigo-600" />
              <span className="text-gray-800">{t.title}</span>
              {t.write && <span className="rounded bg-amber-50 px-1.5 py-0.5 text-[10px] font-medium text-amber-700">yazma</span>}
            </label>
          ))}
        </div>
        {saveM.isError && <p className="mt-2 text-xs text-red-600">{(saveM.error as Error).message}</p>}
      </section>

      {/* Bagli istemciler */}
      <section className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
        <h2 className="mb-3 text-sm font-semibold text-gray-800">Bağlı istemciler</h2>
        {info.connections.length === 0 ? (
          <p className="text-sm text-gray-500">Henüz bağlanan yok. Yukarıdaki adımlarla ChatGPT'den bağlan.</p>
        ) : (
          <div className="divide-y divide-gray-100">
            {info.connections.map((c) => (
              <div key={c.clientId} className="flex items-center gap-3 py-2">
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-gray-800">{c.name}</p>
                  <p className="text-xs text-gray-500">Bağlandı {fmt(c.connectedAt)} · son kullanım {fmt(c.lastUsedAt)}</p>
                </div>
                <button onClick={() => { if (confirm(`${c.name} bağlantısı kesilsin mi? Tekrar bağlanmak için ChatGPT'de yeniden izin vermen gerekir.`)) revokeM.mutate(c.clientId); }}
                  disabled={revokeM.isPending}
                  className="flex items-center gap-1.5 rounded-lg border border-red-200 px-3 py-1.5 text-xs font-medium text-red-600 hover:bg-red-50 disabled:opacity-50">
                  <Unplug className="h-3.5 w-3.5" /> Bağlantıyı kes
                </button>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Surum ve sema senkronu: deploy basarisi "hazir" demek degil — istemci yeni tanimi cekmis mi */}
      {status && (
        <section className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm text-sm">
          <h2 className="mb-2 text-sm font-semibold text-gray-800">Sürüm ve araç şeması</h2>
          <p className="text-gray-600">Sunucu <b>{status.serverVersion}</b> · Talimat <b>{status.workflowVersion}</b> · Canlı şema <b>{status.liveSchemaHash ?? "henüz istemci çekmedi"}</b></p>
          {status.clients.length === 0 ? (
            <p className="mt-1 text-gray-500">Henüz araç listesi çeken istemci yok.</p>
          ) : (
            <ul className="mt-2 space-y-1">
              {status.clients.map((c) => {
                const inSync = c.current && (!status.liveSchemaHash || c.schemaHash === status.liveSchemaHash);
                return (
                  <li key={c.clientId} className={inSync ? "text-green-700" : "text-amber-700"}>
                    {inSync ? "✓" : "⚠"} {info.connections.find((x) => x.clientId === c.clientId)?.name ?? c.clientId.slice(0, 30)} — şema {c.schemaHash}, {fmt(c.fetchedAt)} ({c.fetchedFromVersion ?? "?"} sürümünden)
                    {!inSync && " — araç tanımı canlıdan farklı: ChatGPT'de araçları yenile (bu yetkilendirme sorunu değil)"}
                  </li>
                );
              })}
            </ul>
          )}
          {/* Yetkilendirme kaniti: sunucuya ulasip reddedilen istekler (sema uyusmazligindan ayri) */}
          <p className={`mt-2 ${status.rejectedAuth.length ? "text-red-600" : "text-gray-500"}`}>
            Reddedilen (401) istekler: {status.rejectedAuth.length === 0 ? "yok — yetkilendirme hatasına dair kanıt yok" : `${status.rejectedAuth.length} (son: ${fmt(status.rejectedAuth[0].at)}, token ${status.rejectedAuth[0].hadBearer ? "vardı ama geçersizdi" : "yoktu"})`}
          </p>
        </section>
      )}

      {/* Son cagrilar */}
      <section className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
        <h2 className="mb-3 text-sm font-semibold text-gray-800">Son istekler</h2>
        <p className="mb-2 text-xs text-gray-500">Sunucuya gerçekten ulaşan her istek (bağlantı, araç listesi, araç çağrısı). Argüman, token ve içerik kaydedilmez.</p>
        {logs.length === 0 ? (
          <p className="text-sm text-gray-500">Henüz çağrı yok.</p>
        ) : (
          <div className="max-h-96 overflow-y-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-xs text-gray-500"><th className="py-1 pr-3 font-medium">Zaman</th><th className="py-1 pr-3 font-medium">İstek</th><th className="py-1 pr-3 font-medium">Süre</th><th className="py-1 pr-3 font-medium">Sürüm</th><th className="py-1 font-medium">Sonuç</th></tr></thead>
              <tbody className="divide-y divide-gray-100">
                {logs.map((l) => (
                  <tr key={l.id}>
                    <td className="whitespace-nowrap py-1.5 pr-3 text-gray-500">{fmt(l.createdAt)}</td>
                    <td className="py-1.5 pr-3 text-gray-800" title={l.requestId ? `istek #${l.requestId}` : undefined}>
                      {l.method === "tools/call" ? (info.tools.find((t) => t.name === l.tool)?.title ?? l.tool) : <span className="text-gray-500">{l.method}</span>}
                    </td>
                    <td className="py-1.5 pr-3 text-gray-500">{(l.ms / 1000).toFixed(1)} sn</td>
                    <td className="py-1.5 pr-3 text-xs text-gray-400">{l.serverVersion ?? "—"}</td>
                    <td className="py-1.5">{l.ok ? <span className="text-green-700">Başarılı</span> : <span className="text-red-600" title={l.error ?? ""}>Hata{l.errorCode ? ` (${l.errorCode})` : ""}{l.error ? `: ${l.error.slice(0, 80)}` : ""}</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
